/**
 * TimezoneContext.js - Timezone management with auto-detection
 *
 * Provides user's timezone throughout the app with these features:
 * - Single source of truth (eliminates redundant fetches)
 * - Auto-detection on app launch (detects timezone changes)
 * - User notification when timezone changes
 * - Fallback chain: stored → browser → UTC
 *
 * Usage:
 *   import { useTimezone } from '../context/TimezoneContext';
 *   const timezone = useTimezone(); // e.g., "America/New_York"
 */

import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { useToast } from '@chakra-ui/react';
import { useAuth } from './AuthContext';
import { getTimezone } from '../lib/services/profileService';
import logger from '../utils/logger';

// Create context with UTC default
const TimezoneContext = createContext('UTC');

/**
 * TimezoneProvider - Manages user timezone with auto-detection
 *
 * On mount:
 * 1. Initializes with browser timezone
 * 2. Fetches user's stored timezone from profile
 * 3. Compares stored vs device timezone
 * 4. Auto-updates if different and notifies user
 *
 * @param {{ children: React.ReactNode }} props
 */
export const TimezoneProvider = ({ children }) => {
  const authContext =
    /** @type {{ user?: { id?: string }, updateProfile: (updates: any) => Promise<any> }} */ (
      useAuth()
    );
  const { user, updateProfile } = authContext;
  const toast = useToast();

  // Track if we've already checked timezone for this user session
  const hasCheckedRef = useRef(false);
  const currentUserIdRef = useRef(/** @type {string | null} */ (null));

  // Initialize with browser timezone as immediate fallback
  const [timezone, setTimezone] = useState(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch {
      return 'UTC';
    }
  });

  // Auto-detect timezone changes on app launch
  useEffect(() => {
    if (!user?.id) {
      // Reset check flag when user logs out
      hasCheckedRef.current = false;
      currentUserIdRef.current = null;
      return;
    }

    // Reset check flag if user changed (different user logged in)
    if (currentUserIdRef.current !== user.id) {
      hasCheckedRef.current = false;
      currentUserIdRef.current = user.id;
    }

    // Guard: Only check once per user session to prevent duplicate toasts
    if (hasCheckedRef.current) {
      return;
    }

    let active = true;

    (async () => {
      try {
        // Get current device timezone
        const deviceTz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

        // Get stored timezone from user profile
        const storedTz = await getTimezone(/** @type {string} */ (user.id));

        if (!active) return;

        // Update local state with stored timezone first
        setTimezone(storedTz);

        // Check for timezone mismatch (user traveled or changed system timezone)
        if (storedTz !== deviceTz) {
          logger.info('🌍 Timezone change detected', {
            stored: storedTz,
            device: deviceTz,
            userId: user.id,
          });

          // Update profile in database
          await updateProfile({ timezone: deviceTz });

          // Update local state
          setTimezone(deviceTz);

          // Notify user about the change (only once per session)
          toast({
            title: 'New timezone detected!',
            description: `Adjusted your timezone to ${deviceTz}`,
            status: 'info',
            duration: 5000,
            isClosable: true,
            position: 'top',
          });

          logger.info('✅ Timezone auto-updated', { newTimezone: deviceTz });
        } else {
          logger.debug('✅ Timezone matches (no change needed)', { timezone: storedTz });
        }

        // Mark as checked to prevent duplicate runs
        hasCheckedRef.current = true;
      } catch (error) {
        logger.error('❌ TimezoneContext: Failed to detect/update timezone', error);
        // Keep browser timezone as fallback on error
        const fallbackTz = Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
        if (active) setTimezone(fallbackTz);
        // Mark as checked even on error to prevent spam
        hasCheckedRef.current = true;
      }
    })();

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  return <TimezoneContext.Provider value={timezone}>{children}</TimezoneContext.Provider>;
};

/**
 * useTimezone - Hook to access user's timezone
 *
 * Returns IANA timezone string (e.g., "America/New_York", "Europe/London")
 * Safe to use anywhere inside TimezoneProvider
 *
 * @returns {string} Current user timezone
 */
export const useTimezone = () => useContext(TimezoneContext);
